import React from 'react';
import { useTheme, Fade, Grid, Card, CardContent, Box, Typography, Divider, LinearProgress, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
} from '@mui/icons-material';
import Chart from 'react-apexcharts';

const SurveysTab = ({ data = {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const total = data.total || 0;
  const completionRate = data.completionRate || 0;
  const teacherCompletionRate = data.teacherCompletionRate || 0;
  const edifierCompletionRate = data.edifierCompletionRate || 0;
  const recentSurveys = data.recentSurveys || 0;

  // Horizontal bar chart
  const horizontalBarData = {
    options: {
      chart: {
        type: 'bar',
        horizontal: true,
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '70%',
          borderRadius: 4,
          dataLabels: {
            position: 'right',
            offsetX: 10,
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
        style: { 
          fontSize: '12px', 
          fontWeight: 'bold', 
          colors: ['#333'],
        },
        textAnchor: 'start',
        align: 'right',
      },
      xaxis: {
        categories: ['Overall', 'Teachers', 'Edifiers'],
        max: 100,
        labels: {
          formatter: (val) => `${val}%`,
          style: { fontSize: '12px', fontWeight: 500 },
        },
      },
      yaxis: {
        labels: { 
          style: { fontSize: '12px', fontWeight: 500 },
          align: 'left',
        },
      },
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      colors: ['#1976d2', '#2e7d32', '#9c27b0'],
      legend: { show: false },
    },
    series: [
      {
        name: 'Completion Rate',
        data: [completionRate, teacherCompletionRate, edifierCompletionRate],
      },
    ],
  };

  return (
    <Fade in timeout={500}>
      <Grid container spacing={3}>
        {/* Large Total Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0px 4px 12px 0px rgba(102, 126, 234, 0.3)',
              borderRadius: 2,
              color: 'white',
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <QuizIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
              {total}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500, mb: 2 }}>
              Total Surveys
            </Typography>
            <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Recent (30 days): <strong>{recentSurveys}</strong>
            </Typography>
          </Card>
        </Grid>

        {/* Horizontal Bar Chart */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssessmentIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                Completion Rates by Role
              </Typography>
            </Box>
            <Chart options={horizontalBarData.options} series={horizontalBarData.series} type="bar" height={250} />
          </Card>
        </Grid>

        {/* Overall Completion Widget - 60% width */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              borderTop: '4px solid #1976d2',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                  }}
                >
                  <AssessmentIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                    {completionRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Overall Completion
                  </Typography>
                </Box>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                mb: 2,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: 'primary.main',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/dashboard/survey-responses')}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                }}
              >
                View All
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Teachers and Edifiers Widgets - 40% width */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2.5,
                  background: (theme) => theme.palette.background.paper,
                  boxShadow: (theme) => theme.customShadows.card,
                  borderRadius: 2,
                  borderTop: '4px solid #2e7d32',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon sx={{ fontSize: 20, color: 'success.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Teachers
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                    {teacherCompletionRate.toFixed(1)}%
                  </Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2.5,
                  background: (theme) => theme.palette.background.paper,
                  boxShadow: (theme) => theme.customShadows.card,
                  borderRadius: 2,
                  borderTop: '4px solid #9c27b0',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SupervisorAccountIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Edifiers
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                    {edifierCompletionRate.toFixed(1)}%
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Grid>

      </Grid>
    </Fade>
  );
};

export default SurveysTab;

